import { computed, inject, provide, ref, type InjectionKey } from 'vue';
import {
  defaultPreferences,
  type AccountPreferences,
  type PreferenceMutation,
} from '@atum-remote/preferences';

export interface PreferencesTransport {
  loadPreferences(): Promise<AccountPreferences>;
  writePreferences(
    expectedRevision: number,
    mutation: PreferenceMutation,
  ): Promise<AccountPreferences>;
}
export function createAccountPreferences(transport: PreferencesTransport) {
  const state = ref(defaultPreferences());
  const ready = ref(false);
  const checkedAt = ref(0);
  const error = ref('');
  const pending = ref(0);
  let generation = 0;
  let queue: Promise<unknown> = Promise.resolve();
  function enqueue<T>(work: () => Promise<T>): Promise<T> {
    const current = generation;
    pending.value++;
    const result = queue.then(async () => {
      if (current !== generation) throw new Error('Your account session changed.');
      try {
        return await work();
      } catch (cause) {
        if (current === generation)
          error.value =
            cause instanceof Error ? cause.message : 'Account settings are unavailable.';
        throw cause;
      } finally {
        if (current === generation) pending.value--;
      }
    });
    queue = result.catch(() => {});
    return result;
  }
  function clear() {
    generation++;
    state.value = defaultPreferences();
    ready.value = false;
    checkedAt.value = 0;
    error.value = '';
    pending.value = 0;
    queue = Promise.resolve();
  }
  async function load() {
    const current = generation;
    return enqueue(async () => {
      const next = await transport.loadPreferences();
      if (current === generation) {
        state.value = next;
        ready.value = true;
        checkedAt.value = Date.now();
        error.value = '';
      }
    });
  }
  async function update(mutation: PreferenceMutation, expectedRevision?: number) {
    const current = generation;
    return enqueue(async () => {
      if (!ready.value) throw new Error('Load your account settings before making changes.');
      try {
        const next = await transport.writePreferences(
          expectedRevision ?? state.value.revision,
          mutation,
        );
        if (current === generation) {
          state.value = next;
          checkedAt.value = Date.now();
          error.value = '';
        }
      } catch (cause) {
        // A failed or ambiguous write is never replayed automatically. Refresh for a deliberate retry.
        try {
          const next = await transport.loadPreferences();
          if (current === generation) {
            state.value = next;
            checkedAt.value = Date.now();
          }
        } catch {
          if (current === generation) ready.value = false;
        }
        throw cause;
      }
    });
  }
  return {
    state,
    ready,
    checkedAt,
    error,
    busy: computed(() => pending.value > 0),
    load,
    update,
    clear,
  };
}
export type AccountPreferencesController = ReturnType<typeof createAccountPreferences>;
const key: InjectionKey<AccountPreferencesController> = Symbol('account-preferences');
export const provideAccountPreferences = (value: AccountPreferencesController) =>
  provide(key, value);
export const useAccountPreferences = () => inject(key, undefined);
